import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

const getSlideIndices = (centerIdx: number, total: number) => [
  (centerIdx - 2 + total) % total,
  (centerIdx - 1 + total) % total,
  centerIdx,
  (centerIdx + 1) % total,
  (centerIdx + 2) % total,
];

const getMobileSlideIndices = (centerIdx: number, total: number) => [
  (centerIdx - 1 + total) % total,
  centerIdx,
  (centerIdx + 1) % total,
];

const getTabletSlideIndices = (centerIdx: number, total: number) => [
  (centerIdx - 1 + total) % total,
  centerIdx,
  (centerIdx + 1) % total,
];

interface SliderProps {
  sliderData?: any;
  getMobile?: boolean;
}

const Slider = ({ sliderData }: SliderProps) => {
  const total = sliderData?.length ?? 0;
  const [centerIdx, setCenterIdx] = useState(total >= 3 ? 2 : 0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 767);
      setIsTablet(width >= 767 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (isPaused || total < 3) return;
    intervalRef.current = setInterval(() => {
      setCenterIdx((prev) => (prev + 1) % total);
    }, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, total]);

  if (total < 3) {
    return (
      <div className="flex flex-col items-center">
        <h1>Services Slider</h1>
        <div className="flex gap-4 items-center my-4">
          <div className="flex justify-between items-center gap-0 h-[390px]">
            {sliderData?.map((slide: any) => (
              <div
                key={slide?.id}
                className="flex items-center justify-center rounded w-[292px] h-[292px] bg-green-500 opacity-100"
              >
                <Image
                  src={toWebp(
                    `${baseUrl}${slide?.partner_image?.data?.attributes?.url}`,
                  )}
                  alt={slide?.title}
                  width={262}
                  height={262}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="text-red-500">
          Need at least 3 slides for carousel effect.
        </div>
      </div>
    );
  }

  const indices = isMobile
    ? getMobileSlideIndices(centerIdx, total)
    : isTablet
      ? getTabletSlideIndices(centerIdx, total)
      : getSlideIndices(centerIdx, total);

  const handleNext = () => {
    setIsPaused(true);
    setTimeout(() => {
      setCenterIdx((prev) => (prev + 1) % total);
      setIsPaused(false);
    }, 500);
  };

  const handlePrev = () => {
    setIsPaused(true);
    setTimeout(() => {
      setCenterIdx((prev) => (prev - 1 + total) % total);
      setIsPaused(false);
    }, 500);
  };

  const sizeClasses = isMobile
    ? [
        "w-[252px] h-[252px] min-w-[220px] max-w-[280px] min-h-[220px] max-h-[280px]",
        "w-[300px] h-[300px] min-w-[280px] max-w-[320px] min-h-[280px] max-h-[320px]",
        "w-[252px] h-[252px] min-w-[220px] max-w-[280px] min-h-[220px] max-h-[280px]",
      ]
    : isTablet
      ? [
          "w-[302px] h-[302px] min-w-[302px] max-w-[302px] min-h-[302px] max-h-[302px]",
          "w-[350px] h-[350px] min-w-[350px] max-w-[350px] min-h-[350px] max-h-[350px]",
          "w-[302px] h-[302px] min-w-[302px] max-w-[302px] min-h-[302px] max-h-[302px]",
        ]
      : [
          "w-[292px] h-[292px] min-w-[292px] max-w-[292px] min-h-[292px] max-h-[292px] border border-[#B0D9F7]",
          "w-[342px] h-[342px] min-w-[342px] max-w-[342px] min-h-[342px] max-h-[342px] border border-[#B0D9F7]",
          "w-[390px] h-[390px] min-w-[390px] max-w-[390px] min-h-[390px] max-h-[390px] border border-[#B0D9F7]",
          "w-[342px] h-[342px] min-w-[342px] max-w-[342px] min-h-[342px] max-h-[342px] border border-[#B0D9F7]",
          "w-[292px] h-[292px] min-w-[292px] max-w-[292px] min-h-[292px] max-h-[292px] border border-[#B0D9F7]",
        ];

  return (
    <div className="flex flex-col items-center xl:overflow-visible overflow-hidden pt-10 sm:pt-0">
      <div
        className="flex gap-4 items-center my-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          className={`flex justify-center items-center gap-0 ${isMobile ? "h-[200px]" : "h-[390px]"}`}
        >
          {indices?.map((slideIdx, i) => {
            const slide = sliderData[slideIdx];
            const centerIndex = isMobile || isTablet ? 1 : 2;
            const isCenter = i === centerIndex;

            const slideClass = isCenter
              ? `relative flex items-center justify-center transition-all z-20 border border-border duration-700 grayscale-0 ${sizeClasses[i]}`
              : `relative flex items-center justify-center transition-all z-10 border border-border-light duration-700 grayscale ${sizeClasses[i]}`;

            const bgImageSrc = isCenter
              ? "/images/award-slider-cover.webp"
              : "/images/award-slider-card.webp";

            const imageSize = isMobile ? (isCenter ? 150 : 35) : 262;

            return (
              <div
                key={slide?.id}
                className={slideClass}
                style={{ opacity: isCenter ? 1 : isMobile ? 0.4 : 0.7 }}
              >
                {/* Background Image */}
                <Image
                  src={bgImageSrc}
                  alt="Background"
                  fill
                  className="object-contain"
                  priority={isCenter}
                />

                {/* Foreground partner logo */}
                <div className="relative z-10 flex items-center justify-center">
                  <Image
                    src={toWebp(`${baseUrl}${slide?.partner_image?.data?.attributes?.url}`)}
                    alt={slide?.name}
                    width={imageSize}
                    height={imageSize}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isMobile ? (
        <div className="mt-6 flex justify-center gap-2">
          {/* Custom Pagination */}
          <div className="mt-[30px] lg:mt-[15px] flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative sm:absolute bottom-[15%] lg:bottom-[20%] left-[50%] sm:transform -translate-x-[50%] z-[5]">
            {sliderData.map((_: any, idx: number) => (
              <div
                key={idx}
                onClick={() => setCenterIdx(idx)}
                className={`p-0 relative rounded overflow-hidden ${centerIdx === idx
                    ? " flex w-[80px] h-1 bg-[#E6F3FD]"
                    : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
                  }`}
              >
                <span
                  className={`absolute top-0 rounded left-0  h-1 ${centerIdx === idx
                      ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[4000ms] w-[80px] "
                      : "left-[-100%] w-[16px] "
                    }`}
                ></span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center gap-2 mt-6">
          <button
            onClick={handlePrev}
            className="p-2 border border-border hover:border-text-button transition duration-300 group"
            aria-label="Previous slide"
          >
            <svg
              className="arrow"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.57 5.93L3.5 12l6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-secondary-text group-hover:stroke-text-button"
              />
              <path
                d="M20.5 12H3.67"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-secondary-text group-hover:stroke-text-button"
              />
            </svg>
          </button>
          <span className="text-subtitle-1 font-bold text-secondary-text">
            {centerIdx + 1} / {total}
          </span>
          <button
            onClick={handleNext}
            className="p-2 border border-border hover:border-text-button transition duration-300 group"
            aria-label="Next slide"
          >
            <svg
              className="arrow"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14.43 5.93L20.5 12l-6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-secondary-text group-hover:stroke-text-button"
              />
              <path
                d="M3.5 12h16.83"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-secondary-text group-hover:stroke-text-button"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default Slider;
